import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { selectChannelState, selectConnection, selectPlayers } from '../../state/selectors';
import { setStationId } from '../../slices/local/orchestrator';
import { getPlayer } from '../../middlewares/orchestrator';

// Survives view remounts so a stopped channel can be resumed from anywhere
const lastStationIds: Record<string, number> = {};

/**
 * Play/stop toggle for a channel. Stopping is done by setting the active
 * station ID again; resuming re-selects the last station played on the channel
 * (or `preferredStationId` if given).
 */
export const useStationPlaybackToggle = (playerId: number, channel: number, preferredStationId?: number) => {
  const dispatch = useAppDispatch();
  const players = useAppSelector(selectPlayers);
  const connection = useAppSelector(selectConnection);
  const channelState = useAppSelector(state => selectChannelState(state, { playerId, channel })) || {};
  const player = players[playerId];
  const key = `${playerId}:${channel}`;
  const activeStationId: number | undefined = player?.channels?.[channel]?.stationId;

  useEffect(() => {
    if (activeStationId) {
      lastStationIds[key] = activeStationId;
    }
  }, [key, activeStationId]);

  const isActive = !!activeStationId && (preferredStationId === undefined || activeStationId == preferredStationId);
  const targetStationId = isActive ? activeStationId : (preferredStationId ?? lastStationIds[key]);
  const canToggle = !!player
    && !(player as any).model
    && !!connection?.available
    && player.isOnline
    && !!targetStationId;

  const toggle = () => {
    if (!canToggle) {
      return;
    }

    if (isActive && !getPlayer(channelState, playerId, channel).canBeStopped()) {
      return;
    }

    dispatch(setStationId({
      playerId,
      channel,
      stationId: targetStationId
    }));
  };

  return {
    isActive,
    canToggle,
    toggle
  };
};
